import {
  Alert,
  closeDialog,
  Form,
  FormButton,
  FormField,
  showNotification,
  type ValidationErrors,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useRef, useState} from 'react';

import {
  type ConfigurationDetailResponseBody,
  isFactoryReferenceInUse,
  isNewVehicleConflict,
  useGetConfigurationDetailQuery,
  useRegisterExternalOrderMutation,
} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {composePath, type ApiError, useNavigate, useOnMount} from 'shared/web';

import {getFactoryReferenceInUseMessage} from '../../utils/getFactoryReferenceInUseMessage';
import {importerOrderRefSchema} from '../../utils/importerOrderRefSchema';
import {getNewVehicleEnumLabel} from '../../utils/newVehicleLabels';
import {FactoryReferenceOwnerLink} from '../FactoryReferenceOwnerLink/FactoryReferenceOwnerLink';

export type RegisterExternalOrderDialogProps = {
  configuration: ConfigurationDetailResponseBody;
  dialogId: string;
  onDone?: VoidFunction;
};

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

const orderedOnRequiredMessage = t(
  'entity.newVehicle.external.orderedOnRequired',
  'Ordered on is required'
);
const schema = z.object({
  orderedOn: z
    .string({invalid_type_error: orderedOnRequiredMessage})
    .min(1, orderedOnRequiredMessage),
  stage: z.enum(['ordered', 'in_production', 'in_transit']),
  eta: z.string().nullable(),
  importerOrderRef: importerOrderRefSchema,
  vin: z.string(),
  note: z.string(),
});

type Values = z.infer<typeof schema>;
const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null;
const stages = schema.shape.stage.options.map((value) => ({
  value,
  label: getNewVehicleEnumLabel('orderStatus', value, t),
}));

export function RegisterExternalOrderDialog(props: RegisterExternalOrderDialogProps) {
  const navigate = useNavigate();
  const isMounted = useRef(true);
  useOnMount(
    () => {
      isMounted.current = true;
    },
    () => {
      isMounted.current = false;
    }
  );
  const configurationId = props.configuration.configuration.id;
  const {dialogId} = props;
  const detailQuery = useGetConfigurationDetailQuery({configurationId});
  const detail = detailQuery.currentData ?? props.configuration;
  const [hasConflict, setHasConflict] = useState(false);
  const [ownerVehicleId, setOwnerVehicleId] = useState<string>();
  const [registerExternal, mutation] = useRegisterExternalOrderMutation();

  const submit = (values: Values, setErrors: (errors: ValidationErrors | null) => void) => {
    if (mutation.isLoading || detailQuery.isFetching) {
      return Promise.resolve();
    }
    setOwnerVehicleId(undefined);
    return registerExternal({
      configurationId,
      registerExternalOrderRequestBody: {
        orderedOn: values.orderedOn,
        stage: values.stage,
        eta: values.eta ?? null,
        importerOrderRef: values.importerOrderRef.trim() || null,
        vin: values.vin || null,
        note: values.note || null,
        version: detail.configuration.version,
      },
    })
      .unwrap()
      .then(({vehicleId}) => {
        if (isMounted.current) {
          closeDialog(dialogId);
        }
        props.onDone?.();
        showNotification.success(
          t('entity.newVehicle.notifications.externalOrderRegistered', 'External order registered'),
          {
            layout: 'detailed',
            actions: [
              {
                title: t('entity.newVehicle.actions.openVehicle', 'Open vehicle'),
                onClick: () =>
                  navigate(composePath(vehiclesRoutes.newVehicleDetail, {params: {id: vehicleId}})),
              },
            ],
          }
        );
      })
      .catch((error: unknown) =>
        match(error)
          .when(isFactoryReferenceInUse, (inUse) => {
            setOwnerVehicleId(inUse.data.vehicleId);
            setErrors([
              {
                name: 'importerOrderRef',
                message: getFactoryReferenceInUseMessage(),
              },
            ]);
          })
          .when(isNewVehicleConflict, (conflict) => {
            setHasConflict(true);
            handleApiError(conflict);
            void detailQuery.refetch();
          })
          .when(isApiError, (apiError) => handleApiError(apiError))
          .otherwise(() =>
            showNotification.error(
              t('entity.newVehicle.notifications.orderFailed', 'Vehicle order could not be created')
            )
          )
      );
  };

  return (
    <VStack spacing={4}>
      <Text color="secondary">
        {t(
          'entity.newVehicle.external.helper',
          'Ordered outside Omnetic — delivery stage is maintained manually'
        )}
      </Text>
      <Show when={hasConflict}>
        <Alert
          variant="warning"
          message={t(
            'entity.newVehicle.order.conflict',
            'This configuration changed. Review the refreshed details and try again.'
          )}
        />
      </Show>
      <Form
        experimentalZodSchema={schema}
        defaultValues={{
          orderedOn: '',
          stage: 'ordered',
          eta: null,
          importerOrderRef: '',
          vin: '',
          note: '',
        }}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={[1, 2]}>
              <FormField
                control={control}
                name="orderedOn"
                type="apiDate"
                label={t('entity.newVehicle.order.orderedOn', 'Ordered on')}
                isRequired
              />
              <FormField
                control={control}
                name="stage"
                type="choice"
                label={t('entity.newVehicle.external.stage', 'Delivery stage')}
                options={stages}
                isNotClearable
                menuInPortal
              />
              <FormField
                control={control}
                name="eta"
                type="apiDate"
                label={t('entity.newVehicle.order.eta', 'Estimated arrival')}
              />
              <FormField
                control={control}
                name="importerOrderRef"
                type="text"
                label={t('entity.newVehicle.order.importerReference', 'Importer order reference')}
                onChange={() => setOwnerVehicleId(undefined)}
              />
              <FormField
                control={control}
                name="vin"
                type="text"
                label={i18n.t('entity.vehicle.labels.vin')}
              />
            </Grid>
            <Show when={ownerVehicleId}>
              {(vehicleId) => <FactoryReferenceOwnerLink vehicleId={vehicleId} />}
            </Show>
            <FormField
              control={control}
              name="note"
              type="textarea"
              label={i18n.t('general.labels.note')}
            />
            <HStack justify="flex-end">
              <FormButton
                control={control}
                type="submit"
                title={t('entity.newVehicle.external.submit', 'Register external order')}
                isDisabled={mutation.isLoading || detailQuery.isFetching}
                isLoading={mutation.isLoading || detailQuery.isFetching}
              />
            </HStack>
          </VStack>
        )}
      </Form>
    </VStack>
  );
}
